import React, { useEffect, useState } from 'react';
import { frameworksApi, gapResultsApi } from '../../services/api';
import CustomSelect from '../common/CustomSelect';

const COMPLIANCE_OPTIONS = [
  { value: '', label: 'Not assessed' },
  { value: '1', label: 'Non-compliant' },
  { value: '2', label: 'Partially compliant' },
  { value: '3', label: 'Compliant' },
  { value: '0', label: 'Not applicable' },
];

function AssessmentGapModal({ isOpen, onClose, assessment, oesId, oesName, onSaved, onNotify }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [framework, setFramework] = useState(null);
  const [existing, setExisting] = useState(null);
  const [data, setData] = useState({});

  const frameworkId = assessment?.framework_id;
  const assessmentId = assessment?.id;

  useEffect(() => {
    if (!isOpen || !assessmentId || !frameworkId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [fwRes, gapRes] = await Promise.all([
          frameworksApi.getControls(frameworkId),
          gapResultsApi.getByAssessment(assessmentId, oesId || null).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setFramework(fwRes.data);
        const fwControls = fwRes.data?.controls || [];
        const fromApi = gapRes?.data?.data || {};
        const merged = {};

        // Build a lookup from framework controls by ref
        const controlByRef = {};
        fwControls.forEach((c) => { controlByRef[c.ref] = c; });

        // Gap data keys are subcategory-level refs (e.g. GV.OV-01). Framework controls may use
        // different ref formats (e.g. GV.OC-01.01). Merge using gap data keys as authoritative
        // when they exist, fall back to framework controls for new assessments.
        const gapKeys = Object.keys(fromApi);
        if (gapKeys.length > 0) {
          // Existing gap data — use its keys as the control list
          gapKeys.sort().forEach((ref) => {
            merged[ref] = fromApi[ref] || { value: '', notes: '' };
          });
        } else {
          // No existing gap data — use framework controls
          fwControls.forEach((c) => {
            merged[c.ref] = { value: '', notes: '' };
          });
        }

        setData(merged);
        setExisting(gapRes?.data ?? null);
      } catch (e) {
        if (!cancelled && onNotify) onNotify('error', 'Failed to load framework or results');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isOpen, assessmentId, frameworkId, oesId]);

  const handleControlChange = (controlRef, field, value) => {
    setData((prev) => ({
      ...prev,
      [controlRef]: {
        ...(prev[controlRef] || {}),
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!assessmentId || !frameworkId) return;
    setSaving(true);
    try {
      const dataKeys = Object.keys(data);
      const total = dataKeys.length;
      let assessed = 0;
      let compliant = 0;
      const outData = {};
      Object.keys(data).forEach((ref) => {
        const entry = data[ref];
        // Skip unassessed items (empty value) — only save items with explicit selection
        if (entry?.value === undefined || entry?.value === '') return;
        const v = Number(entry.value);
        outData[ref] = { value: v, notes: entry?.notes || '' };
        if (v > 0) {
          assessed += 1;
          if (v === 3) compliant += 1;
        }
      });
      const progress = total ? Math.round((assessed / total) * 100) : 0;
      const genCompliance = assessed ? Math.round((compliant / assessed) * 100) : 0;
      const meta = {
        assessmentId,
        genCompliance,
        progress,
      };
      await gapResultsApi.save(assessmentId, { meta, data: outData }, oesId || null);
      if (onNotify) onNotify('success', 'Gap analysis saved.');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      if (onNotify) onNotify('error', e?.response?.data?.detail || 'Failed to save gap results');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const controls = framework?.controls || [];
  // Sort by NIST CSF function order (GV → ID → PR → DE → RS → RC), then by ref within function
  const FUNCTION_ORDER = { 'GV': 0, 'ID': 1, 'PR': 2, 'DE': 3, 'RS': 4, 'RC': 5 };
  const dataRefs = Object.keys(data).sort((a, b) => {
    const prefixA = a.split('.')[0] || a.split('-')[0] || '';
    const prefixB = b.split('.')[0] || b.split('-')[0] || '';
    const orderA = FUNCTION_ORDER[prefixA] ?? 99;
    const orderB = FUNCTION_ORDER[prefixB] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b, undefined, { numeric: true });
  });
  const hasData = dataRefs.length > 0;

  // Build lookup from framework controls for enrichment (description, domain, subdomain)
  const controlLookup = {};
  controls.forEach((c) => { controlLookup[c.ref] = c; });

  // Derive domain/subdomain from ref when no framework control matches (e.g. GV.OV-01 → domain=Govern, subdomain=GV.OV)
  const inferDomain = (ref) => {
    const prefix = ref.split('-')[0] || ref.split('.')[0] || '';
    const fnMap = { GV: 'Govern', ID: 'Identify', PR: 'Protect', DE: 'Detect', RS: 'Respond', RC: 'Recover' };
    const fn = fnMap[prefix.split('.')[0]] || prefix;
    return { domain: fn, subdomain: prefix };
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900 bg-opacity-30 z-40" onClick={onClose} aria-hidden="true" />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-eucnet-navy">
            Posture Assessment{oesName ? ` — ${oesName}` : ''} – {framework?.name || '…'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">Loading…</div>
          ) : !framework ? (
            <div className="py-12 text-slate-500 text-center">
              No framework selected for this assessment, or framework not found.
            </div>
          ) : !hasData && controls.length === 0 ? (
            <div className="py-12 text-slate-500 text-center">No controls defined for this framework.</div>
          ) : (
            <div className="space-y-4">
              {dataRefs.map((ref) => {
                const ctrl = controlLookup[ref];
                const inferred = !ctrl ? inferDomain(ref) : null;
                return (
                <div
                  key={ref}
                  className="border border-slate-200 rounded-lg p-4 bg-slate-50/50"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm font-medium text-eucnet-navy">{ref}</div>
                      <div className="text-sm text-slate-600 mt-1">{ctrl ? `${ctrl.domain} / ${ctrl.subdomain}` : `${inferred.domain} / ${inferred.subdomain}`}</div>
                      {ctrl?.description && <p className="text-sm text-slate-700 mt-2">{ctrl.description}</p>}
                    </div>
                    <div className="flex-shrink-0 w-44">
                      <CustomSelect
                        name={`${ref}-value`}
                        value={data[ref]?.value !== undefined && data[ref]?.value !== '' && [0, 1, 2, 3].includes(Number(data[ref].value)) ? String(data[ref].value) : ''}
                        onChange={(e) => handleControlChange(ref, 'value', e.target.value)}
                        options={COMPLIANCE_OPTIONS}
                        placeholder="Status"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <input
                      type="text"
                      placeholder="Notes (optional)"
                      value={data[ref]?.notes || ''}
                      onChange={(e) => handleControlChange(ref, 'notes', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                    />
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {framework && controls.length > 0 && (
          <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-eucnet-sky text-white hover:bg-eucnet-navy disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default AssessmentGapModal;
